<template>
  <div>
    <div class="columns">
      <div class="column is-12">
        <div class="columns">
          <div class="tabs is-boxed">
            <ul>
              <li
                  v-for="(tab, i) in tabs"
                  :class="{ 'is-active': activeTab.key === tab.key }"
                  @click="setActiveTab(tab);"
              >
                <a v-if="evaluate(tab.condition)">
                  <span class="icon is-small">
                      <i
                          class="fa"
                          :class="tab.icon"
                      />
                  </span>
                  <span>{{ $t(`settings.campground.${tab.key}`) }}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div class="edit-box box-shadow">
          <div class="columns">
            <component
              v-if="tabComponents[activeTab.key] && ['images','attributes','lockTimes','documents','rules','campground','payment'].includes(activeTab.key)"
              :is="tabComponents[activeTab.key]"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="modal" :class="{ 'is-active': showCurrencyModal }">
      <div class="modal-background" @click="closeCurrencyModal()"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title" v-html="$t('settings.i18n.currencyModal.title')"></p>
          <button class="delete" @click="closeCurrencyModal()"></button>
        </header>
        <section class="modal-card-body" v-html="$t('settings.i18n.currencyModal.content')"></section>
        <footer class="modal-card-foot is-clearfix">
          <a class="button" @click="closeCurrencyModal()">{{ $t('generic.cancel') }}</a>
          <a class="button is-success" @click="closeCurrencyModal(true)">
                        <span class="icon">
                            <i class="fa fa-check-circle" aria-hidden="true"></i>
                        </span>
            <span>{{ $t('settings.i18n.currencyModal.submit') }}</span>
          </a>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { AppModel } from '../../../models';
// import { Api } from '../../../api';
// import { EventBus } from '../../../utils';
import { cloneDeep } from 'lodash';
// import ArrayFilters from '../../../filters/ArrayFilters';
// Tab components (no lazy loading as requested)
import CampgroundTab from './tabs/CampgroundTab.vue';
import RulesTab from './tabs/RulesTab.vue';
import LockTimesTab from './tabs/LockTimesTab.vue';
import PaymentTab from './tabs/PaymentTab.vue';
import AttributesTab from './tabs/AttributesTab.vue';
import ImagesTab from './tabs/ImagesTab.vue';
import DocumentsTab from './tabs/DocumentsTab.vue';

export default {
    name: 'campground-settings',
    components: {
        // register tabs for direct use if needed
        CampgroundTab,
        RulesTab,
        LockTimesTab,
        PaymentTab,
        AttributesTab,
        ImagesTab,
        DocumentsTab,
    },
    provide() {
        // give children access to parent state/methods without prop drilling
        return {
            settingsParent: this,
        };
    },
    props: [
        'currencyChangeEnabled',
        'currencies',
        'formErrors',
        'languages',
        'value',
    ],
    data() {
        return {
            campground: AppModel.activeCampground,
            owner_frontend_config: {},
            activeTab: {
                key: 'campground',
                icon: '',
            },
            // Tab navigation state
            tabs: [
                {
                    key: 'campground',
                    icon: 'fa-id-card',
                },
                {
                    key: 'rules',
                    icon: 'fa-pencil',
                },
                {
                    key: 'lockTimes',
                    icon: 'fa-lock',
                },
                {
                    key: 'payment',
                    icon: 'fa-credit-card',
                },
                {
                    key: 'attributes',
                    icon: 'fa-star',
                },
                {
                    key: 'images',
                    icon: 'fa-picture-o',
                },
                {
                    key: 'documents',
                    icon: 'fa-file-text',
                },
            ],
            SERVICE_EMAIL_LINK: AppModel.serviceEmailLink,
            showCurrencyModal: false,
            depositInfo: AppModel.depositInfo,
            settingsBeforeChangesApply: null,
            selectedLanguages: [],
            selectedCurrency: null,
            countries: AppModel.countries,
            // map of tabs to components (static, no lazy-loading)
            tabComponents: {
                campground: CampgroundTab,
                rules: RulesTab,
                lockTimes: LockTimesTab,
                payment: PaymentTab,
                attributes: AttributesTab,
                images: ImagesTab,
                documents: DocumentsTab,
            },
        };
    },
    async created() {
        if(this.$route.query.tab) {
            this.activeTab.key = this.$route.query.tab;
        }
        // Initialisieren (falls Daten bereits vorhanden)
        this.onSettingsFetched(this.value);
    },
    mounted() {},
    computed: {
        lang() {
            return AppModel.lang;
        },
    },
    watch: {
        // Wenn die Settings (value) erst nachträglich geladen werden, erneut initialisieren
        value: {
            handler(data) {
                if(!data || typeof data !== 'object') return;
                const hasLangs = Array.isArray(data.languages) && data.languages.length > 0;
                const hasCurrency = !!data.currency;
                if(hasLangs || hasCurrency) {
                    this.onSettingsFetched(data);
                }
            },
            immediate: false,
        },
        // Defensive: Falls Eltern-Props leer sind, aber AppModel bereits Werte hat, über .sync nachziehen
        languages(newVal) {
            if((!Array.isArray(newVal) || newVal.length === 0) && Array.isArray(AppModel.languages) && AppModel.languages.length > 0) {
                this.$emit('update:languages', AppModel.languages);
            }
        },
        currencies(newVal) {
            if((!Array.isArray(newVal) || newVal.length === 0) && Array.isArray(AppModel.currencies) && AppModel.currencies.length > 0) {
                this.$emit('update:currencies', AppModel.currencies);
            }
        },
    },
    methods: {
        evaluate(str) {
            if(typeof str !== 'string') return true;
            // eslint-disable-next-line no-eval
            return eval(str);
        },
        setActiveTab(tab) {
            // avoid redundant navigation and NavigationDuplicated errors
            const currentTab = (this.$route && this.$route.query && this.$route.query.tab) || this.activeTab.key;
            this.activeTab = tab;
            if(currentTab !== tab.key) {
                this.$router.push({ query: { tab: tab.key } }).catch(() => {});
            }
        },
        onSettingsFetched(data) {
            /**
       * warning:
       * This only clones initially fetched data.
       * This is used to handle the currency change and might only be useful in that case because
       * currency change triggers a reload of the page.
       */
            if(!this.settingsBeforeChangesApply && data) {
                this.settingsBeforeChangesApply = cloneDeep(data);
            }
            this.owner_frontend_config = data.owner_frontend_config;
            if(Array.isArray(data.languages)) {
                // populate both source of truth and UI selection
                this.$set(this.value, 'languages', data.languages);
                this.selectedLanguages = [...data.languages];
            }
            if(data.currency) {
                this.selectedCurrency = data.currency;
            }
        },
        closeCurrencyModal(keepCurrency = false) {
            if(!keepCurrency) {
                this.selectedCurrency = this.value.currency;
            }
            this.showCurrencyModal = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.tabs { width: 100%; }
</style>



// WEBPACK FOOTER //
// src/views/components/campground-settings/index.vue